import { useState, memo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import { Box, Flex, Heading, Text, Button, Dialog, CloseButton } from "@libs/design";

const TICK_FONT_SIZE = 12;
const TICK_COLOR = "#6B7280";
const TICK_FONT_WEIGHT = 500;

const YTick = (props: { x?: number; y?: number; payload?: { value: number | string } }) => {
  const { x = 0, y = 0, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="end"
        fill={TICK_COLOR}
        style={{ fontSize: TICK_FONT_SIZE, fontWeight: TICK_FONT_WEIGHT }}
      >
        {payload?.value ?? ""}
      </text>
    </g>
  );
};

const XTick = (props: { x?: number; y?: number; payload?: { value: number | string } }) => {
  const { x = 0, y = 0, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={8}
        textAnchor="middle"
        fill={TICK_COLOR}
        style={{ fontSize: TICK_FONT_SIZE, fontWeight: TICK_FONT_WEIGHT }}
      >
        {payload?.value ?? ""}
      </text>
    </g>
  );
};

const MissingDataTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload?: { country: string; missing: number };
  }>;
}) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const country = item.payload?.country ?? "—";
  const missing = item.value ?? item.payload?.missing ?? 0;
  return (
    <Box
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="md"
      px={3}
      py={2.5}
      minW="100px"
    >
      <Text fontWeight="semibold" color="gray.800" style={{ fontSize: "11px" }}>
        {country}
      </Text>
      <Text mt={1} color="gray.600" style={{ fontSize: "10px" }}>
        Missing: <strong>{missing} days</strong>
      </Text>
    </Box>
  );
};

export type MissingDataChartProps = {
  data: Array<{
    country: string;
    missing: number;
    status: string;
  }>;
};

export const MissingDataChart = memo(function MissingDataChart({ data }: MissingDataChartProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const colors = {
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    link: "#00927B",
    linkHover: "#007961",
  };

  // Soft, professional pastel palette (distinct per country)
  const colorPalette = [
    { from: "#BFDBFE", to: "#60A5FA" }, // soft blue
    { from: "#BBF7D0", to: "#34D399" }, // soft green
    { from: "#FDE68A", to: "#FBBF24" }, // soft amber
    { from: "#E9D5FF", to: "#A855F7" }, // soft purple
    { from: "#FECACA", to: "#F97373" }, // soft red
    { from: "#BAE6FD", to: "#38BDF8" }, // soft cyan
    { from: "#F9A8D4", to: "#EC4899" }, // soft pink
    { from: "#DCFCE7", to: "#22C55E" }, // soft lime
  ];

  const getGradientForIndex = (index: number) => {
    return colorPalette[index % colorPalette.length];
  };

  const renderChart = (height: number, isModal: boolean = false) => {
    const gradientPrefix = isModal ? "modal" : "";
    return (
      <Box w="100%" h={height}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <defs>
              {data.map((_, index) => {
                const palette = getGradientForIndex(index);
                return (
                  <linearGradient
                    key={`gradient-${index}-${gradientPrefix}`}
                    id={`gradient-${index}-${gradientPrefix}`}
                    x1="0"
                    y1="1"
                    x2="0"
                    y2="0"
                  >
                    <stop offset="0%" stopColor={palette.from} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={palette.to} stopOpacity={0.75} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="country"
              type="category"
              tick={<XTick />}
              stroke="#9CA3AF"
              tickMargin={6}
            />
            <YAxis type="number" tick={<YTick />} stroke="#9CA3AF" width={44} />
            <Tooltip content={<MissingDataTooltip />} />
            <Bar dataKey="missing" radius={[4, 4, 0, 0]}>
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={`url(#gradient-${index}-${gradientPrefix})`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading fontSize="md" fontWeight="semibold" color="gray.900">
            Missing Days By Country
          </Heading>
          <Text fontSize="xs" color="gray.500">
            Countries with incomplete data
          </Text>
        </Box>
        <Button
          fontSize="sm"
          variant="ghost"
          fontWeight="medium"
          style={{ color: colors.link }}
          _hover={{
            color: colors.linkHover,
            backgroundColor: "#F3F4F6",
          }}
          _focus={{ boxShadow: "none", outline: "none" }}
          _focusVisible={{ boxShadow: "none", outline: "none" }}
          onClick={() => setIsModalOpen(true)}
        >
          View All →
        </Button>
      </Flex>
      {renderChart(250, false)}

      <Dialog.Root open={isModalOpen} onOpenChange={(e) => setIsModalOpen(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="7xl" style={{ backgroundColor: "#FFFFFF" }}>
            <Dialog.Header>
              <Dialog.Title
                style={{
                  color: colors.textPrimary,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                }}
              >
                All Missing Data By Country
              </Dialog.Title>
              <Dialog.CloseTrigger>
                <CloseButton bg="transparent" />
              </Dialog.CloseTrigger>{" "}
            </Dialog.Header>
            <Dialog.Body>{renderChart(400, true)}</Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
});
